"use client";

import * as React from "react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { CameraPlusIcon } from "@/components/icons";
import { generatePassword } from "@/src/lib/generate-password";
import PasswordGeneratorField from "@/app/groups/components/StudentsTable/StudentDrawerContent/tabs/PasswordGeneratorField";
import {
  useUpdateUserMutation,
  useCreateUserMutation,
  useCreateTeacherMutation,
} from "@/api/user/userApi";
import { useUpdateMeMutation } from "@/api/auth/authPrivateApi";
import type { UpdateMePayload } from "@/api/auth/authPrivateApi";
import { useUserRole } from "@/hooks/useUserRole";
import { isSchoolSupportContactsEnabled } from "@/lib/branding";
import { User } from "@/types/user";

import UserProfileFields from "./UserProfileFields";
import AvatarUploadDialog from "./AvatarUploadDialog";
import ProfilePasswordSection from "./ProfilePasswordSection";
import SchoolSupportContactsSection from "./SchoolSupportContactsSection";
import AstanaDarynFields from "./AstanaDarynFields";
import { isAstanaDarynProfile } from "./astanaDarynTeacher";
import {
  buildCreateStudentPayload,
  buildUpdateMePayload,
  persistedUserTypeFromRole,
  type AuthType,
  type UserRole,
} from "./userProfilePayload";

export type { AuthType, UserRole };

type Props = {
  initialData?: Partial<User>;
  onSubmit?: (data: User) => void;
  showManagerRole?: boolean;
  mode?: "full" | "self-profile";
};

export const UserProfileDrawerContent = React.forwardRef<
  { handleSave: () => void },
  Props
>(({ initialData, onSubmit, showManagerRole = false, mode = "full" }, ref) => {
  const [formState, setFormState] = React.useState<User | null>(
    initialData as User
  );

  const [viewUserType, setViewUserType] = React.useState<UserRole>("student");
  const selfProfileSnapshotRef = React.useRef<Record<string, unknown> | null>(null);

  const [avatarDialogOpen, setAvatarDialogOpen] = React.useState(false);
  const [showErrors, setShowErrors] = React.useState(false);
  const [selectedLanguage, setSelectedLanguage] = React.useState("ru");

  const t = useTranslations();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
  const [updateMe, { isLoading: isUpdatingMe }] = useUpdateMeMutation();
  const [createUser, { isLoading: isCreatingStudent }] =
    useCreateUserMutation();
  const [createTeacher, { isLoading: isCreatingTeacher }] =
    useCreateTeacherMutation();
  const { isManager, isAdmin } = useUserRole();

  React.useEffect(() => {
    const data = (initialData ?? {}) as User;
    const group = (data as any)?.group as string | undefined;

    if (mode === "self-profile") {
      selfProfileSnapshotRef.current = (initialData ?? {}) as Record<string, unknown>;
    } else {
      selfProfileSnapshotRef.current = null;
    }

    const initialUserType = data.user_type as UserRole | undefined;

    const resolvedUserType: UserRole =
      initialUserType === "manager" && !showManagerRole
        ? "teacher"
        : initialUserType ??
        (group === "manager"
          ? showManagerRole
            ? "manager"
            : "teacher"
          : group === "teacher" || group === "reviewer"
            ? "teacher"
            : "student");

    setViewUserType(resolvedUserType);

    /* Пароль генерируем только админскому редактору: в своём профиле смена пароля
       живёт в отдельном блоке «Безопасность» и не уходит вместе с формой. */
    const password =
      mode === "self-profile" ? undefined : data.password || generatePassword();

    setFormState({
      ...data,
      password,
      ...(mode === "self-profile"
        ? {}
        : {
          user_type: persistedUserTypeFromRole(resolvedUserType),
          ...(resolvedUserType === "manager" ? { group: "manager" } : {}),
        }),
      auth_type: (data.auth_type as AuthType | undefined) ?? "password",
    });

    setSelectedLanguage((data as any)?.additional?.language ?? "ru");
    setShowErrors(false);
  }, [initialData, showManagerRole, mode]);

  const user = formState;

  if (!user) {
    return <div>{t("label-loading")}</div>;
  }

  const initials =
    (user.firstname?.[0] ?? "") + (user.lastname?.[0] ?? "");

  const userAdditional = ((user as any).additional ?? {}) as Record<
    string,
    unknown
  >;

  const update = (next: User) => setFormState(next);

  const buildSelfPayload = (overrides: Partial<UpdateMePayload> = {}) =>
    buildUpdateMePayload(
      (selfProfileSnapshotRef.current ?? formState ?? {}) as Record<
        string,
        unknown
      >,
      overrides
    );

  const handleChangePassword = async (password: string): Promise<boolean> => {
    try {
      /* onSubmit здесь не зовём: он закрывает шторку, а профиль не изменился —
         поменялся только пароль, и пользователь остаётся в редакторе. */
      await updateMe(buildSelfPayload({ password })).unwrap();
      toast.success(t("label-success-change-password-description"));
      return true;
    } catch (err: unknown) {
      console.error(
        "[UserProfileDrawerContent:self-profile] Failed to change password",
        err
      );
      toast.error(t("error-change-password-failed"));
      return false;
    }
  };

  const handleSave = async () => {
    if (!formState) return;

    setShowErrors(true);

    const firstname = (formState.firstname ?? "").toString().trim();
    const lastname = (formState.lastname ?? "").toString().trim();

    if (!firstname || !lastname) {
      toast.error(t("error-fill-required-fields"));
      return;
    }

    // --- self profile mode: PUT /user.json (only firstname/lastname/photo) ---
    if (mode === "self-profile") {
      try {
        const current = formState as any;

        const payload = buildSelfPayload({
          firstname,
          lastname,
          patronymic: current.patronymic ?? null,
          photo: current.photo ?? null,
          additional: {
            ...(current.additional ?? {}),
            language: selectedLanguage,
          },
        });

        const updated = await updateMe(payload).unwrap();
        onSubmit?.(updated as any);
        toast.success(t("ui-notification-changes-saved"));
      } catch (err: unknown) {
        console.error("[UserProfileDrawerContent:self-profile] Failed to update me", err);
        toast.error(t("ui-error-create-user"));
      }
      return;
    }

    // --- full editor mode ---
    const roleForSave = viewUserType;
    const requiredAll = roleForSave === "teacher" || roleForSave === "manager";

    const patronymic = (formState.patronymic ?? "").toString().trim();
    const phone = (formState.phone ?? "").toString().trim();

    const rawEmail = (formState.email ?? "").toString().trim();
    const normalizedEmail = rawEmail.length ? rawEmail : null;

    if (requiredAll) {
      if (!patronymic || !phone || !normalizedEmail) {
        toast.error(t("error-fill-required-fields"));
        return;
      }
    }

    if (normalizedEmail) {
      const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
      if (!isValidEmail) {
        toast.error(t("error-email-format"));
        return;
      }
    }

    const fullname = [firstname, lastname, patronymic].filter(Boolean).join("_").toLowerCase();
    const trimmedUsername = (formState.username ?? "").toString().trim();

    const normalizedFormState = {
      ...formState,
      firstname,
      lastname,
      patronymic: patronymic || null,
      phone: phone || null,
      email: normalizedEmail,
      username: trimmedUsername.length ? trimmedUsername : undefined,
      password: (formState as any)._passwordLocked === false ? formState.password : null,
      fullname,
      additional: { ...((formState as any)?.additional ?? {}), language: selectedLanguage },
    } as User;

    const isNew = !normalizedFormState.id;

    try {
      let result: User | null = null;

      if (normalizedFormState.id) {
        // Strip frontend-only flags
        const { _passwordLocked, ...payload } = { ...normalizedFormState } as any;

        if (roleForSave === "manager") {
          (payload as any).group = "manager";
        } else if (roleForSave === "teacher") {
          (payload as any).group = "teacher";
        } else if (roleForSave === "student") {
          (payload as any).group = "student";
        }

        result = (await updateUser(payload).unwrap()) as User;
      } else {
        const createPayload = buildCreateStudentPayload(normalizedFormState);

        if (roleForSave === "manager") {
          (createPayload as any).user = {
            ...(createPayload as any).user,
            group: "manager",
          };
        }

        const isTeacherLike = roleForSave === "teacher" || roleForSave === "manager";

        const created = isTeacherLike
          ? await createTeacher(createPayload as any).unwrap()
          : await createUser(createPayload as any).unwrap();

        result = created as User;
      }

      if (result && onSubmit) {
        onSubmit(result);
      }

      if (isNew) {
        toast.success(t("ui-notification-user-created"));
      } else {
        toast.success(t("ui-notification-changes-saved"));
      }
    } catch (err: unknown) {
      const anyErr = err as any;
      console.error("Failed to save user (raw):", anyErr);
      toast.error(t("ui-error-create-user"));
    }
  };

  React.useImperativeHandle(ref, () => ({
    handleSave,
  }));

  const isCreating = isCreatingStudent || isCreatingTeacher || isUpdatingMe;

  return (
    <div className="flex flex-col gap-6 rounded-lg border p-6 shadow-sm text-sm leading-relaxed">
      {/* Avatar on top */}
      <div className="flex flex-col items-center gap-2 shrink-0">
        <div className="relative">
          <Avatar className="w-24 h-24 object-cover rounded-lg">
            <AvatarImage src={user.photo ?? undefined} />
            <AvatarFallback className="text-lg">{initials || "?"}</AvatarFallback>
          </Avatar>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full bg-white shadow-md border"
            onClick={() => setAvatarDialogOpen(true)}
          >
            <CameraPlusIcon size={16} />
          </Button>
        </div>
      </div>

      {/* Form fields */}
      <div className="w-full flex flex-col gap-6">
        <UserProfileFields
          user={user}
          onChange={update}
          showErrors={showErrors}
          selectedLanguage={selectedLanguage}
          onLanguageChange={setSelectedLanguage}
        />

        {/* Школа и преподаватель нужны только участникам «Астана дарыны» и только
            в своём профиле: в админском редакторе эти поля правит не пользователь. */}
        {mode === "self-profile" && isAstanaDarynProfile(userAdditional) && (
          <AstanaDarynFields
            additional={userAdditional}
            onChange={(next) =>
              setFormState((prev) =>
                prev ? ({ ...prev, additional: next } as User) : prev
              )
            }
          />
        )}

        {/* Каналы поддержки принадлежат школе, поэтому правит их только её менеджер
            в своём профиле, а не админский редактор чужой учётки. */}
        {mode === "self-profile" &&
          (isManager || isAdmin) &&
          isSchoolSupportContactsEnabled() && <SchoolSupportContactsSection />}

        {/* Свой профиль меняет пароль отдельным действием, админский редактор —
            генератором с замком в общей форме. */}
        {mode === "self-profile" ? (
          <ProfilePasswordSection
            onSubmit={handleChangePassword}
            isSubmitting={isUpdatingMe}
          />
        ) : (
          <PasswordGeneratorField
            value={formState}
            onChange={(next) => setFormState(next as User)}
          />
        )}

        {/* Hidden Save Button (triggered via ref) */}
        <div className="hidden">
          <Button onClick={handleSave} disabled={isUpdating || isCreating}>
            {isUpdating || isCreating ? t("ui-saving") : t("btn-save")}
          </Button>
        </div>
      </div>

      <AvatarUploadDialog
        open={avatarDialogOpen}
        onOpenChange={setAvatarDialogOpen}
        onPhotoSelected={(src) =>
          setFormState((prev) => (prev ? { ...prev, photo: src } : prev))
        }
      />
    </div>
  );
});

UserProfileDrawerContent.displayName = "UserProfileDrawerContent";

export default UserProfileDrawerContent;
